import type { NodeSubtype, NodeType } from './processNode';

export type NodeShape = 'circle' | 'hex' | 'rect' | 'bubble' | 'diamond';

export interface NodeUiStyle {
  shape: NodeShape;
  radius?: number;
  width?: number;
  height?: number;
  rx?: number;
  labelOffset?: number;
  palette?: {
    selectedBg?: string;
    selectedText?: string;
  };
}

export interface NodeCatalogEntry {
  type: NodeType;
  subtype: NodeSubtype;
  typeLabel: string;
  subtypeLabel: string;
  label: string;
  icon: string;
  style: NodeUiStyle;
  deletable?: boolean;
}

export const NODE_CATALOG: NodeCatalogEntry[] = [
  { type: 'actor', subtype: 'person', typeLabel: 'actor', subtypeLabel: 'persona', label: 'actor · persona', icon: '🙂', style: { shape: 'circle', radius: 9, labelOffset: 17, palette: { selectedBg: '#4a9e6b', selectedText: '#ffffff' } }, deletable: false },
  { type: 'actor', subtype: 'team', typeLabel: 'actor', subtypeLabel: 'equipo', label: 'actor · equipo', icon: '👥', style: { shape: 'circle', radius: 11, labelOffset: 19 } },
  { type: 'actor', subtype: 'role', typeLabel: 'actor', subtypeLabel: 'rol', label: 'actor · rol', icon: '👤', style: { shape: 'circle', radius: 11, labelOffset: 19 } },
  { type: 'actor', subtype: 'org', typeLabel: 'actor', subtypeLabel: 'organización', label: 'actor · organización', icon: '🏛️', style: { shape: 'rect', width: 30, height: 20, rx: 6, labelOffset: 18 } },
  { type: 'actor', subtype: 'external_party', typeLabel: 'actor', subtypeLabel: 'externo', label: 'actor · externo', icon: '🏢', style: { shape: 'rect', width: 32, height: 20, rx: 6, labelOffset: 18 } },

  { type: 'resource', subtype: 'tool', typeLabel: 'recurso', subtypeLabel: 'herramienta', label: 'recurso · herramienta', icon: '🔧', style: { shape: 'hex', radius: 12, labelOffset: 20 } },
  { type: 'resource', subtype: 'software', typeLabel: 'recurso', subtypeLabel: 'software', label: 'recurso · software', icon: '💻', style: { shape: 'rect', width: 28, height: 18, rx: 5, labelOffset: 17 } },
  { type: 'resource', subtype: 'channel', typeLabel: 'recurso', subtypeLabel: 'canal', label: 'recurso · canal', icon: '💬', style: { shape: 'bubble', width: 26, height: 18, rx: 5, labelOffset: 19 } },

  { type: 'artifact', subtype: 'document', typeLabel: 'artefacto', subtypeLabel: 'documento', label: 'artefacto · documento', icon: '📄', style: { shape: 'rect', width: 28, height: 18, rx: 3, labelOffset: 17 } },
  { type: 'artifact', subtype: 'contract', typeLabel: 'artefacto', subtypeLabel: 'contrato', label: 'artefacto · contrato', icon: '📝', style: { shape: 'rect', width: 30, height: 18, rx: 3, labelOffset: 17 } },
  { type: 'artifact', subtype: 'ticket', typeLabel: 'artefacto', subtypeLabel: 'ticket', label: 'artefacto · ticket', icon: '🎫', style: { shape: 'rect', width: 28, height: 18, rx: 3, labelOffset: 17 } },
  { type: 'artifact', subtype: 'dataset', typeLabel: 'artefacto', subtypeLabel: 'dataset', label: 'artefacto · dataset', icon: '🗃️', style: { shape: 'rect', width: 30, height: 18, rx: 3, labelOffset: 17 } },
  { type: 'artifact', subtype: 'invoice', typeLabel: 'artefacto', subtypeLabel: 'factura', label: 'artefacto · factura', icon: '🧾', style: { shape: 'rect', width: 30, height: 18, rx: 3, labelOffset: 17 } },
  { type: 'artifact', subtype: 'brief', typeLabel: 'artefacto', subtypeLabel: 'brief', label: 'artefacto · brief', icon: '📌', style: { shape: 'rect', width: 28, height: 18, rx: 3, labelOffset: 17 } },
  { type: 'artifact', subtype: 'report', typeLabel: 'artefacto', subtypeLabel: 'reporte', label: 'artefacto · reporte', icon: '📊', style: { shape: 'rect', width: 30, height: 18, rx: 3, labelOffset: 17 } },

  { type: 'event', subtype: 'event', typeLabel: 'evento', subtypeLabel: 'evento', label: 'evento', icon: '⚡', style: { shape: 'diamond', width: 58, height: 36, rx: 10, labelOffset: 28 } },
];

export const NODE_CATALOG_BY_SUBTYPE = NODE_CATALOG.reduce<Record<NodeSubtype, NodeCatalogEntry>>((acc, entry) => {
  acc[entry.subtype] = entry;
  return acc;
}, {} as Record<NodeSubtype, NodeCatalogEntry>);

export const NODE_TYPE_OPTIONS = Array.from(
  NODE_CATALOG.reduce<Map<NodeType, { type: NodeType; label: string; icon: string }>>((acc, entry) => {
    if (!acc.has(entry.type)) {
      acc.set(entry.type, { type: entry.type, label: entry.typeLabel, icon: entry.icon });
    }
    return acc;
  }, new Map())
).map(([, value]) => value);

export const NODE_SUBTYPE_OPTIONS_BY_TYPE = NODE_CATALOG.reduce<Record<NodeType, NodeCatalogEntry[]>>((acc, entry) => {
  acc[entry.type].push(entry);
  return acc;
}, {
  actor: [],
  resource: [],
  artifact: [],
  event: [],
});

export const DEFAULT_SUBTYPE_BY_TYPE: Record<NodeType, NodeSubtype> = {
  actor: 'person',
  resource: 'tool',
  artifact: 'document',
  event: 'event',
};
